/**
 * Central query-key factory. Keys are hierarchical so a broad prefix invalidates
 * everything beneath it — `queryClient.invalidateQueries({ queryKey: keys.live.all })`
 * refreshes every live query, for instance.
 *
 * Filter objects are part of the key, so pass only the fields that change the
 * response, and keep their order stable.
 */
export type QueryFilters = Record<string, unknown>;

export const queryKeys = {
  account: {
    all: ['account'] as const,
    settings: () => [...queryKeys.account.all, 'settings'] as const,
    translations: (accountId: string, language: string) =>
      [...queryKeys.account.all, 'translations', accountId, language] as const,
  },

  sports: {
    all: ['sports'] as const,
    eventsTournament: (filters: QueryFilters) =>
      [...queryKeys.sports.all, 'events', 'tournament', filters] as const,
    eventsCategory: (filters: QueryFilters) =>
      [...queryKeys.sports.all, 'events', 'category', filters] as const,
    event: (eventId: string) => [...queryKeys.sports.all, 'event', eventId] as const,
    betBuilder: (eventId: string) =>
      [...queryKeys.sports.all, 'event', eventId, 'bet-builder'] as const,
    search: (term: string) => [...queryKeys.sports.all, 'search', term] as const,
    topEvents: (filters?: QueryFilters) =>
      [...queryKeys.sports.all, 'top-events', filters ?? {}] as const,
    lastMinute: (filters?: QueryFilters) =>
      [...queryKeys.sports.all, 'last-minute', filters ?? {}] as const,
    dateRange: (filters?: QueryFilters) =>
      [...queryKeys.sports.all, 'date-range', filters ?? {}] as const,
  },

  live: {
    all: ['live'] as const,
    sports: (filters?: QueryFilters) =>
      [...queryKeys.live.all, 'sports', filters ?? {}] as const,
    tournaments: (filters?: QueryFilters) =>
      [...queryKeys.live.all, 'tournaments', filters ?? {}] as const,
    tables: (filters?: QueryFilters) => [...queryKeys.live.all, 'tables', filters ?? {}] as const,
    event: (eventId: string) => [...queryKeys.live.all, 'event', eventId] as const,
  },

  betslip: {
    all: ['betslip'] as const,
    refresh: (selectionIds: readonly string[]) =>
      [...queryKeys.betslip.all, 'refresh', selectionIds] as const,
  },

  player: {
    all: ['player'] as const,
    profile: () => [...queryKeys.player.all, 'profile'] as const,
    balance: () => [...queryKeys.player.all, 'balance'] as const,
    activity: (filters: QueryFilters) => [...queryKeys.player.all, 'activity', filters] as const,
    transactions: (filters: QueryFilters) =>
      [...queryKeys.player.all, 'transactions', filters] as const,
    bonuses: (filters?: QueryFilters) =>
      [...queryKeys.player.all, 'bonuses', filters ?? {}] as const,
    bonusDetail: (bonusType: number | null, bonusId: string | null) =>
      [...queryKeys.player.all, 'bonuses', 'detail', bonusType, bonusId] as const,
    lostBonuses: (filters?: QueryFilters) =>
      [...queryKeys.player.all, 'lost-bonuses', filters ?? {}] as const,
    messages: (filters?: QueryFilters) =>
      [...queryKeys.player.all, 'messages', filters ?? {}] as const,
    messageDetail: (messageId: string | null) =>
      [...queryKeys.player.all, 'messages', 'detail', messageId] as const,
    depositProviders: (filters?: QueryFilters) =>
      [...queryKeys.player.all, 'deposit-providers', filters ?? {}] as const,
    withdrawalProviders: (filters?: QueryFilters) =>
      [...queryKeys.player.all, 'withdrawal-providers', filters ?? {}] as const,
    depositHistory: (filters?: QueryFilters) =>
      [...queryKeys.player.all, 'deposit-history', filters ?? {}] as const,
    withdrawalHistory: (filters?: QueryFilters) =>
      [...queryKeys.player.all, 'withdrawal-history', filters ?? {}] as const,
    depositBonuses: (filters?: QueryFilters) =>
      [...queryKeys.player.all, 'deposit-bonuses', filters ?? {}] as const,
    paymentBanks: (filters?: QueryFilters) =>
      [...queryKeys.player.all, 'payment-banks', filters ?? {}] as const,
    paymentWalletTypes: (filters?: QueryFilters) =>
      [...queryKeys.player.all, 'payment-wallet-types', filters ?? {}] as const,
    paymentAccounts: (filters?: QueryFilters) =>
      [...queryKeys.player.all, 'payment-accounts', filters ?? {}] as const,
  },

  tickets: {
    all: ['tickets'] as const,
    list: (filters: QueryFilters) => [...queryKeys.tickets.all, 'list', filters] as const,
    detail: (ticketId: string) => [...queryKeys.tickets.all, 'detail', ticketId] as const,
    count: () => [...queryKeys.tickets.all, 'count'] as const,
    cashoutList: () => [...queryKeys.tickets.all, 'cashout-list'] as const,
    creditorList: () => [...queryKeys.tickets.all, 'creditor-list'] as const,
    eventResults: (eventIds: readonly number[]) =>
      [...queryKeys.tickets.all, 'event-results', [...eventIds].sort((a, b) => a - b)] as const,
    eventStatistics: (eventIds: readonly number[]) =>
      [
        ...queryKeys.tickets.all,
        'event-statistics',
        [...eventIds].sort((a, b) => a - b),
      ] as const,
  },

  casino: {
    all: ['casino'] as const,
    vendors: (filters: QueryFilters = {}) =>
      [...queryKeys.casino.all, 'vendors', filters] as const,
    games: (filters: QueryFilters) => [...queryKeys.casino.all, 'games', filters] as const,
    categories: (filters: QueryFilters = {}) =>
      [...queryKeys.casino.all, 'categories', filters] as const,
  },

  cms: {
    all: ['cms'] as const,
    pages: (accountGuid: string, language: string) =>
      [...queryKeys.cms.all, 'pages', accountGuid, language] as const,
    page: (slug: string) => [...queryKeys.cms.all, 'page', slug] as const,
  },

  tickers: {
    all: ['tickers'] as const,
    latestWins: () => [...queryKeys.tickers.all, 'latest-wins'] as const,
    biggestWins: () => [...queryKeys.tickers.all, 'biggest-wins'] as const,
  },
} as const;
